import * as THREE from "three";
import { Terrain } from "../terrain";
import { GroundMesh } from "../groundMesh";
import { BridgeFit, fitBridge } from "../bridgeFit";
import { Cell, headingOffset } from "@highmarch/domain";
import { lib } from "./common";

/**
 * The plank bridges over the gorges the road crosses: each span fitted to the gorge as drawn (bridgeFit.ts), built
 * from the kit's `g_bridge_1/2/3` stretched to the gap and pitched to meet the road at both banks, with timber posts
 * from its legs down to the gorge floor (or onto the wall's batter), and the decks giving under the units on them.
 */
export class BridgeView {
  readonly group = new THREE.Group();
  /** Each plank piece in the scene: its model, its rest pose and its sag spring's state. */
  private decks: Deck[] = [];

  /**
   * Fits already made on this level's ground, by crossing: the ground never changes under a level, and a fit probes
   * the gorge walls a few hundred times, so each crossing is fitted once however often the road is rebuilt.
   */
  private readonly fitted = new Map<string, BridgeFit>();

  /** Leaving the level. The post geometry and material are shared for the session and stay. */
  dispose(): void { if (liveDecks === this.decks) liveDecks = []; this.group.clear(); this.decks = []; this.fitted.clear(); }

  /** Fits and builds the bridges of the terrain's current spans (call after the ground is built). */
  rebuild(): void {
    this.group.clear();
    this.decks = [];
    liveDecks = this.decks;
    const T = Terrain.current, L = lib();
    if (!T) return;
    const fits: (BridgeFit | null)[] = [];
    for (const span of T.spans) {
      // fit from the bank the road enters by; a run with no bank before it (the road's portal end) from the far one
      const first = span.cells[0], last = span.cells[span.cells.length - 1];
      const [dx, dz] = headingOffset(span.heading);
      const fromBefore = T.solid(new Cell(first.x - dx, first.y - dz));
      const bank = fromBefore ? { x: first.x - dx, z: first.y - dz, dx, dz } : { x: last.x + dx, z: last.y + dz, dx: -dx, dz: -dz };
      const key = `${bank.x},${bank.z},${bank.dx},${bank.dz},${span.cells.length},${GroundMesh.drawn}`;
      let fit = this.fitted.get(key);
      if (!fit) {
        const reach = span.cells.length + 1 + Fit.Margin;
        const x0 = Math.min(bank.x, bank.x + bank.dx * reach) - Fit.Margin, x1 = Math.max(bank.x, bank.x + bank.dx * reach) + Fit.Margin;
        const z0 = Math.min(bank.z, bank.z + bank.dz * reach) - Fit.Margin, z1 = Math.max(bank.z, bank.z + bank.dz * reach) + Fit.Margin;
        fit = fitBridge({ ...bank, cells: span.cells.length }, GroundMesh.sheetAt, GroundMesh.probeNear(x0, z0, x1, z1));
        this.fitted.set(key, fit);
      }
      fits.push(fit);
      if (L) this.build(fit, L);
    }
    T.setFits(fits);
  }

  private build(f: BridgeFit, L: NonNullable<ReturnType<typeof lib>>): void {
    const yaw = Math.atan2(-f.uz, f.ux);
    for (const p of f.pieces) {
      const name = `g_bridge_${p.model.cells}`;
      if (!L.hq(name)) continue;
      const deck = L.clone(`hq:${name}`);
      deck.rotation.order = "YXZ";                       // yaw first, then the pitch (and the sag's tilt) about Z
      deck.rotation.y = yaw;
      deck.rotation.z = f.pitch;
      deck.scale.x *= p.stretch;
      deck.position.set(p.x, p.y, p.z);
      this.group.add(deck);
      this.decks.push({ model: deck, restY: p.y, pitch: f.pitch, cx: p.x, cz: p.z, ax: f.ux, az: f.uz, half: p.length / 2, dip: 0, dipV: 0, tilt: 0, tiltV: 0 });
    }
    for (const q of f.posts) {
      const h = q.top - q.bottom;
      if (h <= 0) continue;
      const m = new THREE.Mesh(postGeometry ??= new THREE.BoxGeometry(1, 1, 1), postMaterial ??= new THREE.MeshStandardMaterial({ color: Post.Color, roughness: Post.Roughness, metalness: 0 }));
      m.scale.set(Post.Thickness, h, Post.Thickness);
      m.rotation.y = yaw;
      m.position.set(q.x, q.bottom + h / 2, q.z);
      m.castShadow = true;
      m.receiveShadow = true;
      this.group.add(m);
    }
  }

  /**
   * The plank pieces give under the units on them (Phase 2, the gorge crossing): the sag and tilt from `bridgeSag`,
   * reached through a slightly under-damped spring, so a deck bobs once as a unit steps on and settles back when it
   * steps off. Scratch array reused; nothing allocated per frame.
   */
  update(dt: number, enemies: readonly { readonly alive: boolean; readonly x: { toNumber(): number }; readonly y: { toNumber(): number } }[] | null): void {
    if (this.decks.length === 0) return;
    const step = Math.min(dt, 1 / 30);
    for (const d of this.decks) {
      this.along.length = 0;
      if (enemies) {
        for (const e of enemies) {
          if (!e.alive) continue;
          const a = alongDeck(d, e.x.toNumber(), e.y.toNumber());
          if (a !== null) this.along.push(a);
        }
      }
      const want = bridgeSag(this.along, d.half);
      const S = BridgeSag.Stiffness, C = BridgeSag.Damping;
      d.dipV += ((want.dip - d.dip) * S - d.dipV * C) * step; d.dip += d.dipV * step;
      d.tiltV += ((want.tilt - d.tilt) * S - d.tiltV * C) * step; d.tilt += d.tiltV * step;
      d.model.position.y = d.restY - d.dip;
      d.model.rotation.z = d.pitch + d.tilt;
    }
  }
  private readonly along: number[] = [];
}

/** How far round a crossing the gorge walls are probed (world units): past the lip search and the legs' reach. */
const Fit = { Margin: 1.5 } as const;

/** The timber posts: a square section this thick, a dark weathered plank colour. */
const Post = { Thickness: 0.075, Color: 0x5b4130, Roughness: 0.9 } as const;
let postGeometry: THREE.BoxGeometry | null = null;
let postMaterial: THREE.MeshStandardMaterial | null = null;

/** The plank pieces of the road in the scene, as they sag this frame (`deckSagAt`). */
let liveDecks: readonly Deck[] = [];

/** A point's offset along a deck piece from its centre, or null off the piece (its length, a cell wide). */
function alongDeck(d: Deck, x: number, z: number): number | null {
  const a = (x - d.cx) * d.ax + (z - d.cz) * d.az, b = -(x - d.cx) * d.az + (z - d.cz) * d.ax;
  return Math.abs(a) <= d.half && Math.abs(b) <= 0.5 ? a : null;
}

/**
 * How far a plank deck is drawn below its rest height at a world point this frame — its sag, and its tip toward the
 * end a unit stands on — or 0 off every deck. What walks on a deck stands on the planks as drawn (views/enemyFeel.ts).
 */
export function deckSagAt(x: number, z: number): number {
  for (const d of liveDecks) {
    const a = alongDeck(d, x, z);
    if (a !== null) return d.dip - a * Math.sin(d.tilt);
  }
  return 0;
}

interface Deck {
  model: THREE.Object3D; restY: number; pitch: number; cx: number; cz: number;
  /** World direction of the piece's local +X (its length), and half its length in world units. */
  ax: number; az: number; half: number;
  dip: number; dipV: number; tilt: number; tiltV: number;
}

/**
 * How far a plank span sags, and how far it tips, under units at `along` (their offsets from the span's centre along
 * its length, in cells; `half` is half its length). A unit mid-span dips it most; one near an end tips it toward that
 * end. Small on purpose — a few centimetres and a degree or so: rope and planks giving under boots, not a trampoline.
 */
export function bridgeSag(along: readonly number[], half: number): { dip: number; tilt: number } {
  let dip = 0, tilt = 0;
  for (const a of along) {
    const u = Math.max(-1, Math.min(1, a / Math.max(0.5, half)));
    dip += BridgeSag.PerUnit * (1 - 0.6 * u * u);
    tilt -= BridgeSag.TiltPerUnit * u;                   // positive along (+X end) dips that end: negative about Z
  }
  return { dip: Math.min(BridgeSag.MaxDip, dip), tilt: Math.max(-BridgeSag.MaxTilt, Math.min(BridgeSag.MaxTilt, tilt)) };
}
export const BridgeSag = { PerUnit: 0.018, MaxDip: 0.05, TiltPerUnit: 0.012, MaxTilt: 0.025, Stiffness: 120, Damping: 14 };
